import { groupService, themeService } from 'service-container';
import { translate } from 'App/Internationalization';
import BaseForm from 'shared/Forms/BaseForm';
import FieldCheckboxGroup from 'shared/Forms/Fields/FieldCheckboxGroup';
import FieldFile from 'shared/Forms/Fields/FieldFile';
import FieldWebsite from 'shared/Forms/Fields/FieldWebsite';
import FormSuccessMessages from 'shared/Forms/Messages/FormSuccessMessages';
import FormWarningMessages from 'shared/Forms/Messages/FormWarningMessages';
import flags from 'shared/Flags';
import mimeList from '@mooveguru/yhh-shared-config/files/allowed-mimes.json';
import React, { useEffect, useState } from 'react';
import SubmitButton from 'shared/Forms/Inputs/SubmitButton';
import UpgradeButton from 'shared/Forms/Inputs/UpgradeButton';
import useSettings from 'Hooks/Shared/useSettings/useSettings';
import uploadSize from '@mooveguru/yhh-shared-config/files/max-upload.json';

/**
 * @param {import('shared/ThemeService/Theme').default} theme
 * @param {string} groupId
 * @returns {string[]}
 */
function getSelectedGroupIds(theme, groupId) {
	if (theme.sponsor.groupIds.length) {
		return theme.sponsor.groupIds;
	}

	return theme.sponsor.logo || theme.sponsor.redirectUrl ? [groupId] : [];
}

/**
 * @param {{groupId: string; isGated: boolean; subscriptionLink: string}} props
 * @returns {JSX.Element}
 */
export default function SponsorForm(props) {
	const { subscriptions } = useSettings();
	const [groupValues, setGroupValues] = useState(
		/** @type {Record<string, string>} */ ({})
	);
	const [logo, setLogo] = useState(/** @type {File | null} */ (null));
	const [logoPreview, setLogoPreview] = useState(
		/** @type {string | null} */ (null)
	);
	const [message, setMessage] = useState(/** @type {string | null} */ (null));
	const [selectedGroupIds, setSelectedGroupIds] = useState([props.groupId]);
	const [warning, setWarning] = useState(/** @type {string | null} */ (null));
	const [redirectUrl, setRedirectUrl] = useState('');

	useEffect(() => {
		async function load() {
			try {
				const [groups, theme] = await Promise.all([
					groupService.searchGroup('', 1, 100),
					themeService.getGroupTheme(props.groupId),
				]);

				setGroupValues(
					Object.fromEntries(
						groups.data
							.filter(
								(group) =>
									subscriptions.groups
										.get(group.id)
										?.get(flags.manager.coSponsor) ?? false
							)
							.map((group) => [group.id, group.name])
					)
				);
				setSelectedGroupIds(getSelectedGroupIds(theme, props.groupId));
				setLogoPreview(theme.sponsor.logo?.url ?? null);
				setRedirectUrl(theme.sponsor.redirectUrl ?? '');
			} catch (error) {
				setWarning(translate('admin.groups.sponsor.load_error'));
			}
		}

		load();
	}, [props.groupId, subscriptions.groups]);

	/** @param {React.ChangeEvent<HTMLInputElement>} event */
	function handleLogoChange(event) {
		setLogo(event.target.files?.[0] ?? null);
	}

	/** @param {{groupIds?: string[]; redirectUrl: string}} values */
	async function handleSubmit(values) {
		setMessage(null);
		setWarning(null);

		const submittedGroupIds = values.groupIds ?? [];
		const removedGroupIds = selectedGroupIds.filter(
			(groupId) => !submittedGroupIds.includes(groupId)
		);

		try {
			await Promise.all([
				...submittedGroupIds.map(async (groupId) => {
					await themeService.setGroupSponsorRedirectUrl(
						groupId,
						values.redirectUrl,
						submittedGroupIds
					);

					if (logo) {
						await themeService.setGroupSponsorLogo(groupId, logo);
					}
				}),
				...removedGroupIds.map((groupId) =>
					themeService.clearGroupSponsor(groupId)
				),
			]);

			if (removedGroupIds.includes(props.groupId)) {
				setLogoPreview(null);
				setRedirectUrl('');
				setSelectedGroupIds([]);
			} else {
				setSelectedGroupIds(submittedGroupIds);
			}
			setMessage(translate('admin.groups.sponsor.save_success'));
		} catch (error) {
			setWarning(translate('admin.groups.sponsor.save_error'));
		}
	}

	const initialValues = /** @type {any} */ ({
		groupIds: selectedGroupIds,
		redirectUrl,
	});

	return (
		<section className="mb-10">
			<h2 className="h-ms-5 mb-6">
				{translate('admin.groups.sponsor.title')}
			</h2>
			<BaseForm
				enableReinitialize={true}
				initialValues={initialValues}
				noRequiredText={true}
				onSubmit={handleSubmit}
			>
				<FieldFile
					accept={[...Object.values(mimeList.images)]}
					disabled={props.isGated}
					help=""
					inputClass=""
					labelClass=""
					maxSize={uploadSize.image}
					name="sponsorLogo"
					onChange={handleLogoChange}
					preview={logoPreview}
				/>
				<FieldWebsite
					className="mt-6 max-w-md"
					disabled={props.isGated}
					key={redirectUrl}
					label={translate('admin.groups.sponsor.redirect_url')}
					name="redirectUrl"
					required={true}
				/>
				<div className="mt-6">
					<FieldCheckboxGroup
						disabled={props.isGated}
						name="groupIds"
						title={
							<span className="font-bold">
								{translate(
									'admin.groups.sponsor.apply_to_groups'
								)}
							</span>
						}
						values={groupValues}
					/>
				</div>
				{props.isGated ? (
					<UpgradeButton to={props.subscriptionLink} />
				) : (
					<SubmitButton>
						{translate('admin.groups.sponsor.save')}
					</SubmitButton>
				)}
			</BaseForm>
			<FormWarningMessages messages={warning ? [warning] : []} />
			<FormSuccessMessages messages={message ? [message] : []} />
		</section>
	);
}
